// Copyright 2026. WebPros International GmbH. All rights reserved.

import {
    IPaginated,
    IRequest,
} from 'common/api/resources/Request/request';

type IRequestWithPagination = IRequest<object> & IPaginated;

export class QueryBuilder<I extends IRequestWithPagination> {
    private axiosRequestParams: Record<string, string> = {};
    private readonly request: I | IRequestWithPagination;

    constructor(request: I | undefined) {
        this.request = request || {
            filters: {},
        };
    }

    build(): Record<string, string> {
        if (!this.request) {
            return {};
        }

        this.applyFilters();
        this.applyTags();
        this.applyInclude();
        this.applyExclude();
        this.applyPagination();
        this.applyFields();

        const {
            filters,
            tags,
            include,
            exclude,
            page,
            per_page,
            fields,
            ...rest
        } = this.request;

        return {
            ...rest,
            ...this.axiosRequestParams,
        };
    }

    private applyFilters() {
        if (!this.request.filters) {
            return;
        }

        Object.keys(this.request.filters).forEach(key => {
            if (this.request.filters && this.request.filters[key]) {
                const filterValues = this.request.filters[key];

                this.axiosRequestParams[`filter[${key}]`] = Array.isArray(filterValues) ? filterValues.join(',') : filterValues;
            }
        });
    }

    private applyTags() {
        if (!this.request.tags) {
            return;
        }

        Object.keys(this.request.tags).forEach(key => {
            if (this.request.tags![key]) {
                const tagValues = this.request.tags![key];

                this.axiosRequestParams[`tag[${key}]`] = Array.isArray(tagValues) ? tagValues.join(',') : tagValues;
            }
        });
    }

    private applyInclude() {
        if (!this.request.include) {
            return;
        }

        this.axiosRequestParams.include = this.request.include.join(',');
    }

    private applyExclude() {
        if (!this.request.exclude) {
            return;
        }

        this.axiosRequestParams.exclude = this.request.exclude.join(',');
    }

    private applyFields() {
        if (!this.request.fields) {
            return;
        }

        Object.keys(this.request.fields).forEach(key => {
            if (this.request.fields && this.request.fields[key]) {
                const filedValues = this.request.fields[key];

                this.axiosRequestParams[`fields[${key}]`] = Array.isArray(filedValues) ? filedValues.join(',') : filedValues;
            }
        });
    }

    private applyPagination() {
        if (this.request.page) {
            this.axiosRequestParams['page'] = String(this.request.page);
        }

        if (this.request.per_page) {
            this.axiosRequestParams['per_page'] = this.request.per_page === 'all'
                ? '-1'
                : String(this.request.per_page);
        }
    }
}
